import { cn } from '@/lib/utils';
import { Clock } from 'lucide-react';
import React, { useEffect, useState } from 'react';

import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue
} from '@/components/ui/select';

// Quartz cron format: "sec min hour dom month dow"
// sec is always 0; when dow is set, dom must be "?", and vice versa.
// Times are stored in the user's local timezone — the backend handles DST via the timeZone field.

type FrequencyMode = 'hourly' | 'daily' | 'weekdays' | 'weekly' | 'monthly';

const WEEKDAY_CHIPS = [
    { label: 'Mon', value: 1, quartz: 'MON' },
    { label: 'Tue', value: 2, quartz: 'TUE' },
    { label: 'Wed', value: 3, quartz: 'WED' },
    { label: 'Thu', value: 4, quartz: 'THU' },
    { label: 'Fri', value: 5, quartz: 'FRI' },
    { label: 'Sat', value: 6, quartz: 'SAT' },
    { label: 'Sun', value: 0, quartz: 'SUN' }
];

const MINUTES = Array.from({ length: 12 }, (_, i) => String(i * 5));
const HOURS = Array.from({ length: 24 }, (_, i) => String(i));
const MONTH_DAYS = Array.from({ length: 28 }, (_, i) => String(i + 1));

export const USER_TZ = Intl.DateTimeFormat().resolvedOptions().timeZone;

function pad(n: string | number) {
    return String(n).padStart(2, '0');
}

export function getTzAbbr(tz: string): string {
    return (
        new Intl.DateTimeFormat('en-US', { timeZone: tz, timeZoneName: 'short' })
            .formatToParts(new Date())
            .find((p) => p.type === 'timeZoneName')?.value ?? tz
    );
}

function toQuartzDow(days: number[]): string {
    if (days.length === 0) return 'MON';
    return [...days]
        .sort((a, b) => a - b)
        .map((d) => WEEKDAY_CHIPS.find((c) => c.value === d)?.quartz ?? 'MON')
        .join(',');
}

function buildCron(
    mode: FrequencyMode,
    minute: string,
    hour: string,
    day: string,
    selectedDays: number[]
): string {
    const m = minute || '0';
    const h = hour || '8';
    switch (mode) {
        case 'hourly':   return `0 ${m} * * * ?`;
        case 'daily':    return `0 ${m} ${h} * * ?`;
        case 'weekdays': return `0 ${m} ${h} ? * MON-FRI`;
        case 'weekly':   return `0 ${m} ${h} ? * ${toQuartzDow(selectedDays)}`;
        case 'monthly':  return `0 ${m} ${h} ${day || '1'} * ?`;
    }
}

// Collapses selected weekdays into runs (e.g. Mon,Tue,Wed,Thu,Fri -> "Mon - Fri")
// so a full work week doesn't get spelled out day by day.
function formatDayList(selectedDays: number[]): string {
    const selected = WEEKDAY_CHIPS.filter((c) => selectedDays.includes(c.value));
    if (selected.length === 0) return '';

    const groups: (typeof WEEKDAY_CHIPS)[] = [];
    let current: typeof WEEKDAY_CHIPS = [selected[0]];
    for (let i = 1; i < selected.length; i++) {
        const prevIdx = WEEKDAY_CHIPS.indexOf(selected[i - 1]);
        const idx = WEEKDAY_CHIPS.indexOf(selected[i]);
        if (idx === prevIdx + 1) {
            current.push(selected[i]);
        } else {
            groups.push(current);
            current = [selected[i]];
        }
    }
    groups.push(current);

    return groups
        .map((g) =>
            g.length >= 3
                ? `${g[0].label} - ${g[g.length - 1].label}`
                : g.map((c) => c.label).join(', ')
        )
        .join(', ');
}

function humanReadable(
    mode: FrequencyMode,
    minute: string,
    hour: string,
    day: string,
    selectedDays: number[]
): string {
    const time = `${pad(hour)}:${pad(minute)}`;
    switch (mode) {
        case 'hourly':   return `Every hour at minute ${minute}`;
        case 'daily':    return `Every day at ${time}`;
        case 'weekdays': return `Every weekday (Mon–Fri) at ${time}`;
        case 'weekly':   return `Every ${formatDayList(selectedDays)} at ${time}`;
        case 'monthly':  return `Monthly on day ${day} at ${time}`;
    }
}

function parseCron(cron: string): {
    mode: FrequencyMode;
    minute: string;
    hour: string;
    day: string;
    selectedDays: number[];
} {
    const defaults = { minute: '0', hour: '8', day: '1', selectedDays: [1] };
    const parts = cron.trim().split(/\s+/);
    const isQuartz = parts.length === 6;
    const is5Field = parts.length === 5;
    if (!isQuartz && !is5Field) return { mode: 'daily', ...defaults };

    const [min, hr, dom, , dow] = isQuartz ? parts.slice(1) : parts;
    const normalizedMin = /^\d+$/.test(min) ? String(parseInt(min, 10)) : null;
    const minute = normalizedMin !== null && MINUTES.includes(normalizedMin) ? normalizedMin : '0';
    const hour = /^\d+$/.test(hr) ? String(parseInt(hr, 10)) : '8';
    const day = /^\d+$/.test(dom) ? String(parseInt(dom, 10)) : '1';

    if (hr === '*') return { mode: 'hourly', minute, hour: '8', day: '1', selectedDays: [] };
    if (dom === '?' || dom === '*') {
        if (dow === 'MON-FRI' || dow === '1-5')
            return { mode: 'weekdays', minute, hour, day: '1', selectedDays: [] };
        if (dow && dow !== '?' && dow !== '*') {
            const days = dow.split(',').map((name) => {
                const chip = WEEKDAY_CHIPS.find((c) => c.quartz === name.toUpperCase());
                return chip ? chip.value : parseInt(name);
            }).filter((n) => !isNaN(n));
            return { mode: 'weekly', minute, hour, day: '1', selectedDays: days };
        }
    }
    if (/^\d+$/.test(dom) && (dow === '?' || dow === '*'))
        return { mode: 'monthly', minute, hour, day, selectedDays: [] };
    return { mode: 'daily', minute, hour, day: '1', selectedDays: [] };
}

export function cronToHuman(cron: string): string {
    const { mode, minute, hour, day, selectedDays } = parseCron(cron);
    return humanReadable(mode, minute, hour, day, selectedDays);
}

interface Props {
    value: string;
    onChange: (value: string) => void;
}

export const CronPicker: React.FC<Props> = ({ value, onChange }) => {
    const parsed = parseCron(value);

    const [mode, setMode] = useState<FrequencyMode>(parsed.mode);
    const [minute, setMinute] = useState(parsed.minute);
    const [hour, setHour] = useState(parsed.hour);
    const [day, setDay] = useState(parsed.day);
    const [selectedDays, setSelectedDays] = useState<number[]>(
        parsed.selectedDays.length > 0 ? parsed.selectedDays : [1]
    );

    useEffect(() => {
        onChange(buildCron(mode, minute, hour, day, selectedDays));
    }, [mode, minute, hour, day, selectedDays]);

    const toggleDay = (val: number) => {
        setSelectedDays((prev) =>
            prev.includes(val)
                ? prev.length > 1 ? prev.filter((d) => d !== val) : prev
                : [...prev, val]
        );
    };

    const description = humanReadable(mode, minute, hour, day, selectedDays);

    return (
        <div className="space-y-3">
            {/* Frequency + time on one row */}
            <div className="flex flex-wrap items-end gap-3">
                <div className="space-y-1.5">
                    <Label className="text-xs text-muted-foreground">Frequency</Label>
                    <Select
                        value={mode}
                        onValueChange={(v) => setMode(v as FrequencyMode)}
                    >
                        <SelectTrigger className="w-52">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="hourly">Every hour</SelectItem>
                            <SelectItem value="daily">Every day</SelectItem>
                            <SelectItem value="weekdays">Every weekday (Mon–Fri)</SelectItem>
                            <SelectItem value="weekly">Every week — choose days</SelectItem>
                            <SelectItem value="monthly">Every month</SelectItem>
                        </SelectContent>
                    </Select>
                </div>

                {mode === 'monthly' && (
                    <div className="space-y-1.5">
                        <Label className="text-xs text-muted-foreground">On day</Label>
                        <Select value={day} onValueChange={setDay}>
                            <SelectTrigger className="w-20">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {MONTH_DAYS.map((d) => (
                                    <SelectItem key={d} value={d}>{d}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                )}

                {mode === 'hourly' ? (
                    <div className="space-y-1.5">
                        <Label className="text-xs text-muted-foreground">At minute</Label>
                        <Select value={minute} onValueChange={setMinute}>
                            <SelectTrigger className="w-24">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {MINUTES.map((m) => (
                                    <SelectItem key={m} value={m}>:{pad(m)}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                ) : (
                    <>
                        <div className="space-y-1.5">
                            <Label className="text-xs text-muted-foreground">Hour</Label>
                            <Select value={hour} onValueChange={setHour}>
                                <SelectTrigger className="w-20">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {HOURS.map((h) => (
                                        <SelectItem key={h} value={h}>{pad(h)}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <span className="pb-2 text-muted-foreground font-medium">:</span>
                        <div className="space-y-1.5">
                            <Label className="text-xs text-muted-foreground">Minute</Label>
                            <Select value={minute} onValueChange={setMinute}>
                                <SelectTrigger className="w-20">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {MINUTES.map((m) => (
                                        <SelectItem key={m} value={m}>{pad(m)}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </>
                )}
            </div>

            {mode === 'weekly' && (
                <div className="flex gap-1.5">
                    {WEEKDAY_CHIPS.map((chip) => (
                        <Button
                            key={chip.value}
                            type="button"
                            size="compact"
                            variant={
                                selectedDays.includes(chip.value)
                                    ? 'primary'
                                    : 'tertiary'
                            }
                            onClick={() => toggleDay(chip.value)}
                            className="rounded-md font-medium"
                        >
                            {chip.label}
                        </Button>
                    ))}
                </div>
            )}

            <div className="flex items-center gap-1.5 text-muted-foreground text-xs">
                <Clock className="w-3.5 h-3.5 shrink-0" />
                {description}
            </div>
        </div>
    );
};
